// Help Center `iconId` → icon + section heading.
//
// The Help Content API treats `iconId` as a free-form string: there is no
// server-side enum and no lookup endpoint, so an admin can type anything and
// the client owns the mapping entirely. Known ids resolve exactly; anything
// unrecognized falls through a keyword match and finally to a generic icon,
// so a new group added on the admin side still renders sensibly instead of
// showing a blank space where the icon should be.
//
// Confirmed ids in use: account, repairs, reports, billing, printers, sales,
// other. The rest are speculative but cheap to carry, and correcting one is a
// one-line change here.

// Normalizes "Purchase_Orders", "purchase-orders", "purchaseOrders" → "purchaseorders"
const normalize = (iconId) =>
  String(iconId ?? "")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z\d]/g, "");

// Exact matches, checked first. `icon` is an ICON_ALIASES key (never a raw SF
// Symbol name); `label` overrides the auto-generated heading where title-casing
// the id alone would read badly.
const EXACT = {
  // ── Confirmed in the live data ──────────────────────────────────────────────
  account: { icon: "helpAccount", label: "Account" },
  repairs: { icon: "helpRepairs", label: "Repairs" },
  reports: { icon: "helpReports", label: "Reports" },
  billing: { icon: "helpBilling", label: "Billing" },
  printers: { icon: "helpPrinters", label: "Printers" },
  sales: { icon: "helpSales", label: "Sales" },
  other: { icon: "helpGeneral", label: "Other" },

  // ── Plausible future groups ────────────────────────────────────────────────
  gettingstarted: { icon: "helpGettingStarted", label: "Getting Started" },
  setup: { icon: "helpGettingStarted", label: "Setup" },
  onboarding: { icon: "helpGettingStarted", label: "Onboarding" },
  repair: { icon: "helpRepairs", label: "Repairs" },
  tickets: { icon: "helpTickets", label: "Tickets" },
  ticket: { icon: "helpTickets", label: "Tickets" },
  checkout: { icon: "helpSales", label: "Checkout" },
  orders: { icon: "helpOrders", label: "Orders" },
  purchaseorders: { icon: "helpOrders", label: "Purchase Orders" },
  payments: { icon: "helpPayments", label: "Payments" },
  invoices: { icon: "helpBilling", label: "Invoices" },
  subscription: { icon: "helpSubscription", label: "Subscription" },
  plans: { icon: "helpSubscription", label: "Plans" },
  inventory: { icon: "helpInventory", label: "Inventory" },
  items: { icon: "helpInventory", label: "Items" },
  parts: { icon: "helpInventory", label: "Parts" },
  devices: { icon: "helpDevices", label: "Devices" },
  customers: { icon: "helpCustomers", label: "Customers" },
  employees: { icon: "helpEmployees", label: "Employees" },
  team: { icon: "helpEmployees", label: "Team" },
  appointments: { icon: "helpAppointments", label: "Appointments" },
  calendar: { icon: "helpAppointments", label: "Calendar" },
  analytics: { icon: "helpReports", label: "Analytics" },
  settings: { icon: "helpSettings", label: "Settings" },
  store: { icon: "helpStore", label: "Store" },
  shop: { icon: "helpStore", label: "Shop" },
  locations: { icon: "helpStore", label: "Locations" },
  integrations: { icon: "helpIntegrations", label: "Integrations" },
  notifications: { icon: "helpNotifications", label: "Notifications" },
  messaging: { icon: "helpNotifications", label: "Messaging" },
  marketing: { icon: "helpMarketing", label: "Marketing" },
  shipping: { icon: "helpShipping", label: "Shipping" },
  hardware: { icon: "helpPrinters", label: "Hardware" },
  labels: { icon: "helpBarcodes", label: "Labels" },
  barcodes: { icon: "helpBarcodes", label: "Barcodes" },
  pricing: { icon: "helpPricing", label: "Pricing" },
  labor: { icon: "helpPricing", label: "Labor" },
  warranty: { icon: "helpWarranty", label: "Warranty" },
  diagnostics: { icon: "helpDiagnostics", label: "Diagnostics" },
  security: { icon: "helpSecurity", label: "Security" },
  general: { icon: "helpGeneral", label: "General" },
  faq: { icon: "helpGeneral", label: "FAQ" },
  misc: { icon: "helpGeneral", label: "Other" },
};

// Substring fallbacks for ids that aren't an exact hit ("printerSetup",
// "billing_and_payments"). Order matters — the first match wins, so more
// specific stems are listed ahead of the generic ones they contain.
const KEYWORDS = [
  ["gettingstarted", "helpGettingStarted"],
  ["purchaseorder", "helpOrders"],
  ["subscription", "helpSubscription"],
  ["appointment", "helpAppointments"],
  ["integration", "helpIntegrations"],
  ["notification", "helpNotifications"],
  ["diagnostic", "helpDiagnostics"],
  ["employee", "helpEmployees"],
  ["inventory", "helpInventory"],
  ["customer", "helpCustomers"],
  ["marketing", "helpMarketing"],
  ["analytic", "helpReports"],
  ["shipping", "helpShipping"],
  ["warranty", "helpWarranty"],
  ["security", "helpSecurity"],
  ["checkout", "helpSales"],
  ["password", "helpSecurity"],
  ["setting", "helpSettings"],
  ["barcode", "helpBarcodes"],
  ["printer", "helpPrinters"],
  ["payment", "helpPayments"],
  ["invoice", "helpBilling"],
  ["billing", "helpBilling"],
  ["account", "helpAccount"],
  ["repair", "helpRepairs"],
  ["device", "helpDevices"],
  ["report", "helpReports"],
  ["ticket", "helpTickets"],
  ["label", "helpBarcodes"],
  ["order", "helpOrders"],
  ["price", "helpPricing"],
  ["pricing", "helpPricing"],
  ["sales", "helpSales"],
  ["sale", "helpSales"],
  ["store", "helpStore"],
  ["shop", "helpStore"],
  ["login", "helpAccount"],
  ["plan", "helpSubscription"],
  ["part", "helpInventory"],
  ["item", "helpInventory"],
  ["team", "helpEmployees"],
  ["labor", "helpPricing"],
  ["email", "helpNotifications"],
  ["sms", "helpNotifications"],
];

const FALLBACK_ICON = "helpGeneral";

/** ICON_ALIASES key for an iconId. Always returns something renderable. */
export const helpIconFor = (iconId) => {
  const key = normalize(iconId);
  if (!key) return FALLBACK_ICON;
  if (EXACT[key]) return EXACT[key].icon;

  const hit = KEYWORDS.find(([stem]) => key.includes(stem));
  return hit ? hit[1] : FALLBACK_ICON;
};

// "purchase_orders" / "purchaseOrders" → "Purchase Orders". Used when the id
// isn't in EXACT, so the heading still reads like a heading rather than a key.
const titleCase = (iconId) =>
  String(iconId ?? "")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** Human-readable section heading for an iconId. */
export const helpLabelFor = (iconId) => {
  const key = normalize(iconId);
  if (EXACT[key]) return EXACT[key].label;
  return titleCase(iconId) || "Other";
};
